import { useState } from 'react';
import { Link } from 'react-router-dom';
import { retailPartners, distributionAreas, regions, partnerCategories } from '../../mocks/retailers';
import PartnerCard from './components/PartnerCard';

export default function WhereToBuyPage() {
  const [activeRegion, setActiveRegion] = useState('all');
  const [activeCategory, setActiveCategory] = useState('all');
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);

  const filteredPartners = retailPartners.filter((p) => {
    const regionMatch = activeRegion === 'all' || p.region === activeRegion;
    const catMatch = activeCategory === 'all' || p.type === activeCategory;
    return regionMatch && catMatch;
  });

  const totalStores = distributionAreas.reduce((acc, d) => acc + d.stores, 0);
  const totalDistributors = distributionAreas.reduce((acc, d) => acc + d.distributors, 0);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/1ae6d47aa9f5caec142554cc0371daf1/04683b963241abfa25e88f4f0de2b164.jpeg"
                alt="Freeze Dried Philippines Logo"
                className="w-10 h-10 object-contain"
              />
              <span className="text-lg font-bold text-gray-900">FREEZE DRIED PHILIPPINES</span>
            </Link>
            <div className="flex items-center gap-7">
              <Link to="/" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
              <Link to="/products" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Products</Link>
              <Link to="/where-to-buy" className="text-sm font-semibold text-amber-800 whitespace-nowrap cursor-pointer">Where to Buy</Link>
              <Link to="/#contact" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Contact</Link>
              <a href="/#contact" className="bg-amber-900 text-white text-sm font-semibold px-5 py-2 rounded-lg hover:bg-amber-800 transition-all whitespace-nowrap cursor-pointer">Get a Quote</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/cfab810bb8f9e9bff2fea3d227bfc164.jpg"
            alt="Philippines Map"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-amber-900/70 via-amber-900/50 to-white"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 px-4 py-2 rounded-full mb-6">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-fill text-white text-xs"></i>
            </div>
            <span className="text-white text-sm font-medium">Available Nationwide</span>
          </div>
          <h1 className="text-6xl font-bold text-white mb-4 leading-tight">WHERE TO BUY</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-10">
            Find Freeze Dried Philippines products at leading supermarkets, health stores, and online platforms across the country.
          </p>
          <div className="flex items-center justify-center gap-10">
            <div className="text-center">
              <div className="text-5xl font-bold text-white">{totalStores}+</div>
              <div className="text-white/80 text-sm mt-1">Retail Locations</div>
            </div>
            <div className="w-px h-12 bg-white/30"></div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white">{totalDistributors}+</div>
              <div className="text-white/80 text-sm mt-1">Distributors</div>
            </div>
            <div className="w-px h-12 bg-white/30"></div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white">4</div>
              <div className="text-white/80 text-sm mt-1">Major Regions</div>
            </div>
            <div className="w-px h-12 bg-white/30"></div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white">500+</div>
              <div className="text-white/80 text-sm mt-1">Retail Partners</div>
            </div>
          </div>
        </div>
      </section>

      {/* Distribution Coverage Map */}
      <section className="py-20 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">DISTRIBUTION COVERAGE</h2>
            <p className="text-gray-600 text-lg">We deliver across all major islands of the Philippines</p>
          </div>

          <div className="grid grid-cols-2 gap-8 items-start">
            {/* Map Embed */}
            <div className="rounded-2xl overflow-hidden border border-amber-200" style={{ height: '480px' }}>
              <iframe
                title="Freeze Dried Philippines Distribution Areas"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d8134540.494760427!2d116.84023835!3d12.099569999999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397ca03571cd1e7%3A0x24071bca2f7c5613!2sPhilippines!5e0!3m2!1sen!2sph!4v1700000000000!5m2!1sen!2sph"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            {/* Region Cards */}
            <div className="grid grid-cols-2 gap-4">
              {distributionAreas.map((area) => (
                <div
                  key={area.id}
                  className={`${area.color} rounded-2xl p-6 cursor-pointer transition-all duration-200 border-2 ${
                    hoveredRegion === area.id ? 'border-amber-400 scale-[1.02]' : 'border-transparent'
                  }`}
                  onMouseEnter={() => setHoveredRegion(area.id)}
                  onMouseLeave={() => setHoveredRegion(null)}
                  onClick={() => setActiveRegion(area.id)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">{area.region}</h3>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${
                      area.coverage === 'Full Coverage' ? 'bg-green-200 text-green-800' :
                      area.coverage === 'Wide Coverage' ? 'bg-amber-200 text-amber-800' :
                      'bg-gray-200 text-gray-700'
                    }`}>
                      {area.coverage}
                    </span>
                  </div>
                  <div className="flex gap-4 mb-4">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{area.stores}</div>
                      <div className="text-xs text-gray-600">Store Locations</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{area.distributors}</div>
                      <div className="text-xs text-gray-600">Distributors</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {area.cities.slice(0, 4).map((city) => (
                      <span key={city} className="text-xs bg-white/60 text-gray-700 px-2 py-0.5 rounded-full">
                        {city}
                      </span>
                    ))}
                    {area.cities.length > 4 && (
                      <span className="text-xs text-gray-500">+{area.cities.length - 4} more</span>
                    )}
                  </div>
                  <button
                    onClick={() => setActiveRegion(area.id)}
                    className="mt-4 text-xs font-semibold text-amber-800 flex items-center gap-1 cursor-pointer whitespace-nowrap hover:gap-2 transition-all"
                  >
                    View Partners <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partner Directory */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">FIND A RETAILER</h2>
              <p className="text-gray-600">Browse our network of retail partners and distributors</p>
            </div>
            <span className="text-sm text-gray-500">{filteredPartners.length} partner{filteredPartners.length !== 1 ? 's' : ''} found</span>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-4 mb-10">
            {/* Region Filter */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">Region</span>
              <div className="flex gap-2 flex-wrap">
                {regions.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setActiveRegion(r.id)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeRegion === r.id
                        ? 'bg-amber-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-amber-100 hover:text-amber-900'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">Type</span>
              <div className="flex gap-2 flex-wrap">
                {partnerCategories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setActiveCategory(c.id)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeCategory === c.id
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Partners Grid */}
          {filteredPartners.length > 0 ? (
            <div className="grid grid-cols-4 gap-5">
              {filteredPartners.map((partner) => (
                <PartnerCard key={partner.id} partner={partner} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-gray-50 rounded-2xl">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 text-gray-300">
                <i className="ri-store-2-line text-4xl"></i>
              </div>
              <p className="text-gray-500 text-lg font-medium">No partners found in this area yet.</p>
              <p className="text-gray-400 text-sm mt-1">We&apos;re expanding! Check back soon or contact us for distribution inquiries.</p>
            </div>
          )}
        </div>
      </section>

      {/* Become a Retail Partner */}
      <section className="py-20 bg-gradient-to-br from-amber-900 to-amber-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-amber-300 text-sm font-semibold uppercase tracking-widest mb-3">Partner With Us</p>
              <h2 className="text-5xl font-bold text-white mb-6 leading-tight">BECOME A RETAIL PARTNER</h2>
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                Join our growing network of retail partners across the Philippines. We offer competitive wholesale pricing, reliable supply, and full marketing support for your store. Whether you run a supermarket, health food store, or online shop — we&apos;d love to have you on board.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: 'ri-truck-line', label: 'Reliable Delivery', desc: 'On-time supply with cold chain logistics' },
                  { icon: 'ri-price-tag-3-line', label: 'Wholesale Pricing', desc: 'Competitive tiered pricing for retailers' },
                  { icon: 'ri-customer-service-line', label: 'Dedicated Support', desc: 'Dedicated account manager for your store' },
                  { icon: 'ri-award-line', label: 'FDA Registered', desc: 'All products are certified and compliant' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 mt-0.5 shrink-0">
                      <i className={`${item.icon} text-amber-300`}></i>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{item.label}</p>
                      <p className="text-white/60 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <a
                href="/#contact"
                className="inline-flex items-center gap-2 bg-white text-amber-900 font-bold px-8 py-4 rounded-xl hover:bg-amber-50 transition-all whitespace-nowrap cursor-pointer"
              >
                APPLY TO BECOME A PARTNER
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden" style={{ height: '460px' }}>
                <img
                  src="https://readdy.ai/api/search-image?query=Filipino%20retail%20store%20owner%20proudly%20displaying%20premium%20freeze%20dried%20fruit%20products%20on%20well-organized%20store%20shelves%20bright%20clean%20modern%20store%20interior%20warm%20natural%20lighting%20professional%20photography&width=700&height=460&seq=wtb002&orientation=landscape"
                  alt="Retail Partner"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 border border-amber-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg">
                    <i className="ri-store-2-fill text-green-700"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">500+ Partners</p>
                    <p className="text-xs text-gray-500">and growing every month</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-100 py-10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/1ae6d47aa9f5caec142554cc0371daf1/04683b963241abfa25e88f4f0de2b164.jpeg"
              alt="Freeze Dried Philippines Logo"
              className="w-8 h-8 object-contain"
            />
            <span className="text-sm font-bold text-gray-700">FREEZE DRIED PHILIPPINES</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-sm text-gray-500 hover:text-gray-900 transition-colors cursor-pointer">Home</Link>
            <Link to="/products" className="text-sm text-gray-500 hover:text-gray-900 transition-colors cursor-pointer">Products</Link>
            <Link to="/where-to-buy" className="text-sm text-gray-500 hover:text-gray-900 transition-colors cursor-pointer">Where to Buy</Link>
            <a href="/#contact" className="text-sm text-gray-500 hover:text-gray-900 transition-colors cursor-pointer">Contact</a>
          </div>
          <p className="text-xs text-gray-400">© 2025 Freeze Dried Philippines. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
